import { useState, useEffect } from "react";
import { MapPin, X } from "lucide-react";
import { Modal, FormField, Input, Button } from "../ui/index";
import AddressAutocomplete from "../ui/AddressAutocomplete";
import { MapContainer, TileLayer, useMapEvents, useMap, Marker, Polygon } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { cascadeAPI, membresAPI } from "../../api/services";
import MemberMultiSelect from "../ui/MemberMultiSelect";
import { useAuth } from "../../context/AuthContext";

const pinIcon = L.divIcon({
  html: `<div style="width:12px;height:12px;background:#3b82f6;border:2px solid white;border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`,
  className: "", iconSize: [12, 12], iconAnchor: [6, 6],
});

function MapClicker({ points, onAddPoint }) {
  useMapEvents({ click(e) { onAddPoint([e.latlng.lat, e.latlng.lng]); } });
  return (
    <>
      {points.map((p, i) => <Marker key={i} position={p} icon={pinIcon} />)}
      {points.length >= 3 && (
        <Polygon positions={points} pathOptions={{ color: "#3b82f6", fillColor: "#3b82f6", fillOpacity: 0.15, weight: 2 }} />
      )}
    </>
  );
}

function FlyToLocation({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) map.flyTo([lat, lng], 16, { duration: 1.5 });
  }, [lat, lng, map]);
  return null;
}

function FitToPoints({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points && points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 18 });
    }
  }, [points, map]);
  return null;
}

export default function EditProjetModal({ isOpen, onClose, onEdit, item }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ nom: "", revendeur: "", client: "", email: "", adresse: "", dateDebut: "", etat: "En cours de création" });
  const [chefDeProjet, setChefDeProjet] = useState([]);
  const [responsableTechnique, setResponsableTechnique] = useState([]);
  const [points, setPoints] = useState([]);
  const [flyTo, setFlyTo] = useState({ lat: null, lng: null });
  const [errors, setErrors] = useState({});

  // Données dynamiques
  const [revendeurs, setRevendeurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [membres, setMembres] = useState([]);

  // Prefill form when item changes
  useEffect(() => {
    if (!item) return;
    setApiError("");
    setSaving(false);
    const revendeurId = item.revendeur?._id || item.revendeur || "";
    const clientId = item.client?._id || item.client || "";
    setForm({
      nom: item.nom || "",
      revendeur: revendeurId,
      client: clientId,
      email: item.email || "",
      adresse: item.adresse || "",
      dateDebut: item.dateDebut ? item.dateDebut.slice(0, 10) : "",
      etat: item.etat || "En cours de création",
    });
    setChefDeProjet((item.chefDeProjet || []).map(m => m._id || m));
    setResponsableTechnique((item.responsableTechnique || []).map(m => m._id || m));
    setPoints(item.points || []);
    setFlyTo({ lat: null, lng: null });
  }, [item]);

  // Charger revendeurs au montage
  useEffect(() => {
    if (!isOpen) return;
    cascadeAPI.revendeurs().then(res => setRevendeurs(res.data.data || res.data || [])).catch(console.error);
  }, [isOpen]);

  // Charger membres quand revendeur change
  useEffect(() => {
    if (!isOpen) return;
    const params = {};
    if (user?.role === 'admin' && form.revendeur) params.revendeur = form.revendeur;
    membresAPI.getAll(params).then(res => {
      setMembres(res.data.data || res.data || []);
    }).catch(console.error);
  }, [isOpen, form.revendeur, user?.role]);

  // Charger clients quand revendeur change (tous si pas de revendeur)
  useEffect(() => {
    cascadeAPI.clients(form.revendeur || undefined).then(res => setClients(res.data.data || res.data || [])).catch(console.error);
  }, [form.revendeur]);

  const handleChange = (field) => (e) => {
    const val = e.target.value;
    if (field === "revendeur") setForm(f => ({ ...f, revendeur: val, client: "", email: "" }));
    else if (field === "client") {
      const selectedClient = clients.find(c => c._id === val);
      setForm(f => ({ ...f, client: val, email: selectedClient?.email || f.email }));
    }
    else setForm(f => ({ ...f, [field]: val }));
  };

  const addPoint = (p) => setPoints(prev => [...prev, p]);

  const handleAddressSelect = ({ address, lat, lng }) => {
    setForm(f => ({ ...f, adresse: address }));
    setFlyTo({ lat, lng });
  };

  const [apiError, setApiError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    const errs = {};
    if (!form.nom?.trim()) errs.nom = "Nom du projet requis";
    if (!form.client) errs.client = "Client requis";
    if (!form.adresse?.trim()) errs.adresse = "Adresse requise";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    setApiError("");
    try {
      await onEdit?.({ ...form, revendeur: form.revendeur || null, points, chefDeProjet, responsableTechnique });
      onClose();
    } catch (err) {
      setApiError(err.response?.data?.error || err.message || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const selectStyle = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifier le projet" subtitle="Modifiez les informations du projet.">
      <div className="space-y-4">

        {/* Nom */}
        <FormField label="Nom du projet" required error={errors.nom}>
          <Input value={form.nom} onChange={handleChange("nom")} placeholder="Nom du projet" />
        </FormField>

        {/* Revendeur + Client */}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Revendeur">
            <select value={form.revendeur} onChange={handleChange("revendeur")} className={selectStyle}>
              <option value="">Sélectionner un revendeur</option>
              {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
            </select>
          </FormField>
          <FormField label="Client" required error={errors.client}>
            <select value={form.client} onChange={handleChange("client")} className={selectStyle}>
              <option value="">Sélectionner un client</option>
              {clients.map(c => <option key={c._id} value={c._id}>{c.nom}</option>)}
            </select>
          </FormField>
        </div>

        {/* Affectation équipe par rôle */}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Chef(s) de projet">
            <MemberMultiSelect
              membres={membres}
              selected={chefDeProjet}
              onChange={setChefDeProjet}
              defaultRole="Chef de Projet"
              tagColor="bg-blue-50 text-blue-700"
            />
          </FormField>
          <FormField label="Responsable(s) technique">
            <MemberMultiSelect
              membres={membres}
              selected={responsableTechnique}
              onChange={setResponsableTechnique}
              defaultRole="Responsable Technique"
              tagColor="bg-orange-50 text-orange-700"
            />
          </FormField>
        </div>

        {/* Email */}
        <FormField label="Email du projet">
          <Input value={form.email} onChange={handleChange("email")} placeholder="exemple@gmail.com" type="email" />
        </FormField>

        {/* Adresse avec autocomplete */}
        <FormField label="Adresse" required error={errors.adresse}>
          <AddressAutocomplete
            value={form.adresse}
            onChange={(val) => setForm(f => ({ ...f, adresse: val }))}
            onSelect={handleAddressSelect}
            placeholder="Rechercher une adresse..."
          />
        </FormField>

        {/* Carte */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Délimitation du territoire</label>
          <div className="flex items-center gap-1.5 text-xs text-blue-500 mb-2">
            <MapPin size={12} /> Cliquez sur la carte pour tracer le périmètre du projet
            {points.length > 0 && (
              <button onClick={() => setPoints([])} className="ml-2 text-red-400 hover:text-red-600">
                Réinitialiser ({points.length} pts)
              </button>
            )}
          </div>
          <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: 220 }}>
            <MapContainer center={points.length > 0 ? points[0] : [46.5, 2.5]} zoom={points.length > 0 ? 16 : 5} style={{ height: "100%", width: "100%" }} zoomControl={true}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
              <MapClicker points={points} onAddPoint={addPoint} />
              <FlyToLocation lat={flyTo.lat} lng={flyTo.lng} />
              <FitToPoints points={points} />
            </MapContainer>
          </div>

          {/* Coordonnées éditables */}
          {points.length > 0 && (
            <div className="mt-2 space-y-1.5 max-h-32 overflow-y-auto">
              {points.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="text-slate-400 w-6 text-right font-mono">{i + 1}.</span>
                  <input
                    type="number"
                    step="any"
                    value={p[0]}
                    onChange={(e) => {
                      const updated = [...points];
                      updated[i] = [parseFloat(e.target.value) || 0, p[1]];
                      setPoints(updated);
                    }}
                    className="w-28 px-2 py-1 border border-gray-200 rounded-lg text-xs bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Latitude"
                  />
                  <input
                    type="number"
                    step="any"
                    value={p[1]}
                    onChange={(e) => {
                      const updated = [...points];
                      updated[i] = [p[0], parseFloat(e.target.value) || 0];
                      setPoints(updated);
                    }}
                    className="w-28 px-2 py-1 border border-gray-200 rounded-lg text-xs bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Longitude"
                  />
                  <button
                    type="button"
                    onClick={() => setPoints(points.filter((_, j) => j !== i))}
                    className="p-1 hover:bg-red-50 rounded transition-colors"
                  >
                    <X size={12} className="text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Date + État */}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Date de début" required>
            <input type="date" value={form.dateDebut} onChange={handleChange("dateDebut")}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </FormField>
          <FormField label="État" required>
            <select value={form.etat} onChange={handleChange("etat")} className={selectStyle}>
              <option>En cours de création</option>
              <option>Actif</option>
              <option>En pause</option>
              <option>Terminé</option>
            </select>
          </FormField>
        </div>

        {/* Erreur API */}
        {apiError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 font-medium">
            {apiError}
          </div>
        )}

        {/* Boutons */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? "Enregistrement..." : "Enregistrer"}</Button>
        </div>
      </div>
    </Modal>
  );
}
